import { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RotateCcw, Lightbulb, ArrowRight, Check, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import useStore from '../store/useStore';

// All possible chord names for autocomplete
const ALL_CHORDS = [
  'C', 'Cm', 'Cdim',
  'C#', 'C#m', 'C#dim',
  'Db', 'Dbm', 'Dbdim',
  'D', 'Dm', 'Ddim',
  'D#', 'D#m', 'D#dim',
  'Eb', 'Ebm', 'Ebdim',
  'E', 'Em', 'Edim',
  'F', 'Fm', 'Fdim',
  'F#', 'F#m', 'F#dim',
  'Gb', 'Gbm', 'Gbdim',
  'G', 'Gm', 'Gdim',
  'G#', 'G#m', 'G#dim',
  'Ab', 'Abm', 'Abdim',
  'A', 'Am', 'Adim',
  'A#', 'A#m', 'A#dim',
  'Bb', 'Bbm', 'Bbdim',
  'B', 'Bm', 'Bdim'
];

// Enharmonic equivalents mapping
const ENHARMONIC_MAP = {
  'C#': ['Db', 'C#'],
  'Db': ['C#', 'Db'],
  'D#': ['Eb', 'D#'],
  'Eb': ['D#', 'Eb'],
  'F#': ['Gb', 'F#'],
  'Gb': ['F#', 'Gb'],
  'G#': ['Ab', 'G#'],
  'Ab': ['G#', 'Ab'],
  'A#': ['Bb', 'A#'],
  'Bb': ['A#', 'Bb'],
};

// Normalize chord name for comparison
const normalizeChord = (chord) => {
  if (!chord) return '';
  let normalized = chord.trim()
    .replace(/\s+/g, '')
    .replace(/minor/gi, 'm')
    .replace(/min/gi, 'm')
    .replace(/major/gi, '')
    .replace(/maj/gi, '')
    .replace(/diminished/gi, 'dim')
    .replace(/flat/gi, 'b')
    .replace(/sharp/gi, '#')
    .replace(/♯/g, '#')
    .replace(/♭/g, 'b');
  
  if (normalized.length > 0) {
    normalized = normalized[0].toUpperCase() + normalized.slice(1).toLowerCase();
    normalized = normalized.replace(/DIM$/i, 'dim').replace(/M$/i, 'm');
  }
  
  return normalized;
};

// Check if two chords are equivalent (including enharmonic)
const chordsAreEqual = (chord1, chord2) => {
  const n1 = normalizeChord(chord1);
  const n2 = normalizeChord(chord2);
  
  if (n1 === n2) return true;
  
  const getRoot = (c) => {
    if (c.length >= 2 && (c[1] === '#' || c[1] === 'b')) {
      return c.substring(0, 2);
    }
    return c[0];
  };
  
  const getQuality = (c) => {
    const root = getRoot(c);
    return c.substring(root.length);
  };
  
  const root1 = getRoot(n1);
  const root2 = getRoot(n2);
  const quality1 = getQuality(n1);
  const quality2 = getQuality(n2);
  
  if (quality1 !== quality2) return false;
  
  const equivalents = ENHARMONIC_MAP[root1];
  if (equivalents && equivalents.includes(root2)) {
    return true;
  }
  
  return false;
};

const Flashcard = ({ question, onAnswer, onNext }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [userAnswer, setUserAnswer] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [hint, setHint] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [loadingHint, setLoadingHint] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const { timerEnabled, timerDuration } = useStore();
  const [timeLeft, setTimeLeft] = useState(timerDuration);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Reset state when question changes
  useEffect(() => {
    setIsFlipped(false);
    setUserAnswer(null);
    setInputValue('');
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(0);
    setHint('');
    setShowHint(false);
    setFeedback(null);
    setTimeLeft(timerDuration);
    
    if (question.type === 'number_to_chord' && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [question, timerDuration]);

  // Timer logic
  useEffect(() => {
    if (!timerEnabled || isFlipped || feedback) return;
    
    if (timeLeft <= 0) {
      setFeedback('incorrect');
      onAnswer(false);
      setIsFlipped(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 0.1);
    }, 100);

    return () => clearInterval(timer);
  }, [timerEnabled, timeLeft, isFlipped, feedback, onAnswer]);

  // Filter suggestions based on input
  useEffect(() => {
    if (inputValue.length > 0 && question.type === 'number_to_chord' && !feedback) {
      const filtered = ALL_CHORDS.filter(chord => 
        chord.toLowerCase().startsWith(inputValue.toLowerCase())
      ).slice(0, 6);
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedIndex(0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [inputValue, question.type, feedback]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const flipCard = useCallback(() => {
    if (!isFlipped && !feedback) {
      setIsFlipped(true);
      setFeedback('revealed');
    }
  }, [isFlipped, feedback]);

  // Handle chord answer
  const handleChordAnswer = (chord) => {
    if (feedback) return;
    
    setInputValue(chord);
    setShowSuggestions(false);
    setUserAnswer(chord);
    
    const correctAnswer = question.answer;
    const isCorrect = chordsAreEqual(chord, correctAnswer);
    
    setFeedback(isCorrect ? 'correct' : 'incorrect');
    onAnswer(isCorrect);
    setIsFlipped(true);
  };

  const handleSelectSuggestion = (chord) => {
    handleChordAnswer(chord);
  };

  const handleInputKeyDown = (e) => {
    if (feedback) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (showSuggestions && suggestions[selectedIndex]) {
        handleSelectSuggestion(suggestions[selectedIndex]);
      } else if (inputValue) {
        handleChordAnswer(inputValue);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleNumberAnswer = (answer) => {
    if (feedback) return;
    
    const numAnswer = parseInt(answer, 10);
    const correctAnswer = parseInt(question.answer, 10);
    
    setUserAnswer(numAnswer);
    const isCorrect = numAnswer === correctAnswer;
    
    setFeedback(isCorrect ? 'correct' : 'incorrect');
    onAnswer(isCorrect);
    setIsFlipped(true);
  };

  const handleSelfAssess = (wasCorrect) => {
    onAnswer(wasCorrect);
    setFeedback(wasCorrect ? 'correct' : 'incorrect');
  };

  const fetchHint = async () => {
    if (hint || loadingHint) return;
    setLoadingHint(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/hint`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          key: question.key,
          number: question.number,
          question_type: question.type
        })
      });
      const data = await response.json();
      setHint(data.hint);
      setShowHint(true);
    } catch (e) {
      setHint("Think about where this chord sits in the scale.");
      setShowHint(true);
    }
    setLoadingHint(false);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (document.activeElement === inputRef.current) return;
      
      if (e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        if (!feedback) flipCard();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (feedback && feedback !== 'revealed') onNext();
      } else if (e.key >= '1' && e.key <= '7' && question.type === 'chord_to_number' && !feedback) {
        handleNumberAnswer(parseInt(e.key));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [question, feedback, flipCard, onNext]);

  const renderQuestionContent = () => {
    if (question.type === 'number_to_chord') {
      return (
        <>
          <span className="text-muted-foreground text-lg md:text-xl mb-2">
            Key of {question.key}
          </span>
          <span className="font-mono text-6xl md:text-8xl font-black text-foreground">
            {question.number}
          </span>
          <span className="text-muted-foreground text-base mt-4">
            What chord is this?
          </span>
        </>
      );
    } else {
      return (
        <>
          <span className="text-muted-foreground text-lg md:text-xl mb-2">
            Key of {question.key}
          </span>
          <span className="font-mono text-5xl md:text-7xl font-black text-foreground">
            {question.chord}
          </span>
          <span className="text-muted-foreground text-base mt-4">
            What number is this?
          </span>
        </>
      );
    }
  };

  const renderAnswerContent = () => {
    const { answerDetails } = question;
    const isAnswered = feedback === 'correct' || feedback === 'incorrect';
    
    return (
      <>
        {isAnswered && (
          <span className={cn(
            "text-lg md:text-xl font-semibold mb-2",
            feedback === 'correct' ? "text-emerald-500" : "text-rose-500"
          )}>
            {feedback === 'correct' ? 'Correct!' : 'Incorrect'}
          </span>
        )}
        {feedback === 'revealed' && (
          <span className="text-muted-foreground text-lg mb-2">
            The answer is:
          </span>
        )}
        <span className="font-mono text-5xl md:text-7xl font-black text-foreground">
          {question.type === 'number_to_chord' ? answerDetails.chordName : question.answer}
        </span>
        <span className="text-muted-foreground text-base mt-2">
          {answerDetails.roman} • {answerDetails.quality} chord
        </span>
        {question.type === 'number_to_chord' && (
          <span className="text-muted-foreground text-sm mt-1">
            {question.number} in {question.key} = {answerDetails.chordName}
          </span>
        )}
        {feedback === 'incorrect' && userAnswer && (
          <span className="text-muted-foreground text-sm mt-2">
            You answered: {userAnswer}
          </span>
        )}
      </>
    );
  };

  const renderChordInput = () => {
    if (question.type !== 'number_to_chord' || feedback) return null;
    
    return (
      <div className="mt-6 w-full max-w-xs mx-auto" data-testid="chord-input-container">
        <div className="relative">
          <Input
            ref={inputRef}
            type="text"
            placeholder="Type chord name..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleInputKeyDown}
            onFocus={() => inputValue && setSuggestions(ALL_CHORDS.filter(c => 
              c.toLowerCase().startsWith(inputValue.toLowerCase())
            ).slice(0, 6))}
            className="text-center text-xl font-mono h-12"
            autoComplete="off"
            data-testid="chord-input"
          />
          
          <AnimatePresence>
            {showSuggestions && suggestions.length > 0 && (
              <motion.div
                ref={suggestionsRef}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute top-full left-0 right-0 mt-1 bg-card border-2 border-border rounded-xl shadow-lg overflow-hidden z-50"
                data-testid="suggestions-dropdown"
              >
                {suggestions.map((chord, index) => (
                  <button
                    key={chord}
                    onClick={() => handleSelectSuggestion(chord)}
                    className={cn(
                      "w-full px-4 py-3 text-left font-mono text-lg transition-colors",
                      index === selectedIndex 
                        ? "bg-accent text-white" 
                        : "hover:bg-secondary"
                    )}
                    data-testid={`suggestion-${chord}`}
                  >
                    {chord}
                  </button>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        <p className="text-xs text-muted-foreground mt-2 text-center">
          Start typing to see suggestions
        </p>
      </div>
    );
  };

  const renderNumberButtons = () => {
    if (question.type !== 'chord_to_number' || feedback) return null;
    
    return (
      <div className="mt-6" data-testid="number-answer-section">
        <div className="flex gap-2 flex-wrap justify-center" data-testid="number-buttons">
          {[1, 2, 3, 4, 5, 6, 7].map(num => (
            <button
              key={num}
              onClick={() => handleNumberAnswer(num)}
              className={cn(
                "piano-key w-12 h-14 text-xl font-mono btn-press",
                userAnswer === num && (
                  num === parseInt(question.answer, 10)
                    ? "bg-emerald-100 border-emerald-500 dark:bg-emerald-900"
                    : "bg-rose-100 border-rose-500 dark:bg-rose-900"
                )
              )}
              data-testid={`answer-btn-${num}`}
            >
              {num}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center w-full max-w-lg mx-auto" data-testid="flashcard-container">
      {/* Timer bar */}
      {timerEnabled && !feedback && (
        <div className="w-full h-2 bg-muted rounded-full mb-4 overflow-hidden" data-testid="timer-bar">
          <motion.div
            className="h-full bg-accent rounded-full"
            initial={{ width: '100%' }}
            animate={{ width: `${(timeLeft / timerDuration) * 100}%` }}
            transition={{ duration: 0.1, ease: 'linear' }}
          />
        </div>
      )}

      {/* Flashcard */}
      <div 
        className={cn(
          "flashcard",
          isFlipped && "flipped",
          feedback === 'correct' && "flash-success",
          feedback === 'incorrect' && "shake-error"
        )}
        onClick={() => !isFlipped && !feedback && flipCard()}
        data-testid="flashcard"
      >
        <div className="flashcard-inner">
          {/* Front */}
          <div className="flashcard-face">
            {renderQuestionContent()}
          </div>
          {/* Back */}
          <div className="flashcard-face flashcard-back">
            {renderAnswerContent()}
          </div>
        </div>
      </div>

      {/* Chord input (typing) for number_to_chord mode */}
      {renderChordInput()}

      {/* Number buttons for chord_to_number mode */}
      {renderNumberButtons()}

      {/* Self-assessment buttons after reveal */}
      {feedback === 'revealed' && (
        <div className="flex gap-3 mt-6" data-testid="self-assess-buttons">
          <span className="text-sm text-muted-foreground mr-2 self-center">Did you know it?</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSelfAssess(false)}
            className="btn-press border-rose-300 text-rose-600 hover:bg-rose-50"
            data-testid="self-assess-no"
          >
            <X className="w-4 h-4 mr-1" />
            No
          </Button>
          <Button
            size="sm"
            onClick={() => handleSelfAssess(true)}
            className="btn-press bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="self-assess-yes"
          >
            <Check className="w-4 h-4 mr-1" />
            Yes
          </Button>
        </div>
      )}

      {/* Hint section */}
      <AnimatePresence>
        {showHint && hint && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="mt-4 p-4 bg-secondary/50 backdrop-blur-sm rounded-xl text-sm text-muted-foreground max-w-md"
            data-testid="hint-display"
          >
            <Lightbulb className="inline w-4 h-4 mr-2 text-amber-500" />
            {hint}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action buttons */}
      <div className="flex gap-3 mt-6" data-testid="action-buttons">
        {!feedback && !isFlipped && (
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchHint}
              disabled={loadingHint}
              className="btn-press"
              data-testid="hint-btn"
            >
              <Lightbulb className="w-4 h-4 mr-2" />
              {loadingHint ? 'Loading...' : 'Hint'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={flipCard}
              className="btn-press"
              data-testid="reveal-btn"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reveal
            </Button>
          </>
        )}
        {feedback && feedback !== 'revealed' && (
          <Button
            onClick={onNext}
            className="btn-press bg-accent hover:bg-accent/90 text-accent-foreground"
            data-testid="next-btn"
          >
            Next Card
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>

      {/* Keyboard shortcuts hint */}
      <div className="mt-4 text-xs text-muted-foreground" data-testid="keyboard-hints">
        <span className="hidden md:inline">
          {question.type === 'number_to_chord' ? (
            <>Type chord, <kbd className="px-1 py-0.5 bg-muted rounded text-xs font-mono">↑↓</kbd> to navigate, <kbd className="px-1 py-0.5 bg-muted rounded text-xs font-mono ml-1">Enter</kbd> to submit</>
          ) : (
            <><kbd className="px-1 py-0.5 bg-muted rounded text-xs font-mono">1-7</kbd> to answer, <kbd className="px-1 py-0.5 bg-muted rounded text-xs font-mono ml-1">Enter</kbd> for next</>
          )}
        </span>
      </div>
    </div>
  );
};

export default Flashcard;
